// web/src/store/global/globalThunks.ts
import toastMessage from '@/components/Toast';
import apiInstance from '@api/index';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { clearAllFilters } from '@store/global/globalSlice';
import { isDef } from '@utils/def';

import { ThunkApiConfig } from 'models/ThunkApiConfig';

import { RootState } from '../types';
import { getThunkError } from '../utility/thunkErrorUtils';

// Define the FilterOption interface if not defined elsewhere
interface FilterOption {
  label: string;
  value: string;
}

// Thunk to fetch Sponsors
export const fetchSponsors = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string; state: RootState }
>(
  'filters/fetchSponsors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.filters.fetchSponsorList();
      const data: any[] = response.data.data;
      const sponsors: FilterOption[] = data.map((item) => ({
        ...item,
        label: item.comp_name ?? '',
        value: item.company_id ?? '',
      }));
      return sponsors;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch sponsors');
    }
  },
  {
    // Collapse overlapping dispatches (e.g. the global Filters bar and a page
    // both fetching on mount) into a single in-flight request.
    condition: (_, { getState }) => !getState().global.sponsorsLoading,
  },
);

// Thunk to fetch Instruments
export const fetchInstruments = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('filters/fetchInstruments', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchInstrumentList();
    const data: any[] = response.data.data;
    const instruments: FilterOption[] = data.map((item) => ({
      label: item.instrument_type__name ?? '',
      value: item.instrument_type__instrument_id ?? '',
    }));
    return instruments;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch instruments');
  }
});

// Thunk to fetch Currencies
export const fetchCurrencies = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('filters/fetchCurrencies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchCurrencyList();
    const data: any[] = response.data.data;
    const currencies: FilterOption[] = data.map((item) => ({
      label: item.currency_code ?? item.description ?? '', // choose what you want to show
      value: String(item.currency_id), // convert to string to match your Select component
    }));
    return currencies;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch currencies');
  }
});

// Thunk to fetch Counterparties
export const fetchCounterparties = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('filters/fetchCounterparties', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchCounterPartyList();
    const data: any[] = response.data.data;
    const counterparties: FilterOption[] = data.map((item) => ({
      label: item.bk_entity__bk_entity_name ?? '',
      value: item.bk_entity__bk_entity_id ?? '',
    }));
    return counterparties;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch counterparties');
  }
});

// Thunk to fetch Company Entities
export const fetchCompanyEntities = createAsyncThunk<
  FilterOption[],
  string | null,
  { rejectValue: string }
>('filters/fetchCompanyEntities', async (id, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchCompanyEntityList(id);
    const data: any[] = response.data.data;
    const companyEntities: FilterOption[] = data.map((item) => ({
      ...item,
      label: item.comp_entity_name ?? '',
      value: item.comp_entity_id ?? '', // Ensure uniqueness
    }));
    return companyEntities;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch company entities');
  }
});

// Thunk to fetch Companies
export const fetchCompanies = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('filters/fetchCompanies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchCompanyList();
    const data: any[] = response.data.data;
    const companies: FilterOption[] = data.map((item) => ({
      ...item,
      label: item.comp_name ?? '',
      value: item.company_id ?? '',
    }));
    return companies;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch companies');
  }
});

// Thunk to fetch Clusters
export const fetchClusters = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('filters/fetchClusters', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchClusterList();
    const data: any[] = response.data.data;
    const clusters: FilterOption[] = data.map((item) => ({
      label: item.cluster__cluster_name ?? '',
      value: item.cluster__id ?? '',
    }));
    return clusters;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch clusters');
  }
});

// Thunk to fetch Classifications
export const fetchClassifications = createAsyncThunk<
  FilterOption[],
  void,
  { rejectValue: string }
>('filters/fetchClassifications', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchClassificationsList(); // Ensure this method exists
    const data: any[] = response.data.data;
    const classifications: FilterOption[] = data.map((item) => ({
      label: item.trade_tag__description ?? '',
      value: item.trade_tag__id ?? '',
    }));
    return classifications;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch classifications');
  }
});
/*
export const getCurrentUserPermissions = createAsyncThunk<
  { permissions: string[] },
  void,
  { rejectValue: string }
>(
  'permissions/get-current-user-permissions',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.permissions.fetchCurrentUserPermissions();
      return isDef(response.data.data)
        ? { permissions: response.data?.data }
        : { permissions: [] };
    } catch (error: any) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'An unknown error occurred in the permission request for current user',
      );
    }
  },
);
*/
export const getUserPermissions = createAsyncThunk<
  { permissions: string[] },
  { userId: string },
  { rejectValue: string }
>(
  'permissions/check-user-permission',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.permissions.fetchUserPermissions({
        userId,
      });

      return isDef(response.data.data)
        ? { permissions: response.data?.data }
        : { permissions: [] };
    } catch (error: any) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'An unknown error occurred in the permission request',
      );
    }
  },
);

export const setClearAllFilters = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'filters/clearAllFilters',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState().auth;
      dispatch(clearAllFilters({ value: state.user.currencyId, label: '' }));
      return { value: state.user.currencyId, label: '' };
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch classifications',
      );
    }
  },
);

export const fetchTransactions = createAsyncThunk<
  [],
  void,
  { rejectValue: string }
>('transactions/fetchTransactions', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchTrasnactionList();
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch transactions');
  }
});

export const fetchAllTransactions = createAsyncThunk<
  [],
  void,
  { rejectValue: string }
>('transactions/fetchAllTransactions', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.filters.fetchAllTradesList();
    return response.data.data;
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message);
    return rejectWithValue(error.message || 'Failed to fetch transactions');
  }
});

export const fetchActiveTransactionIds = createAsyncThunk<
  number[],
  void,
  { rejectValue: string }
>('transactions/fetchActiveTransactionIds', async (_, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.transactions.getActiveTransactionIdList();
    return response.data.data;
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message);
    return rejectWithValue(
      error.message || 'Failed to fetch active transaction IDs',
    );
  }
});
